// api/tutor.ts — streaming tutor API clients (SSE).
// Exports: explainWrongAnswerStream, chiediStream
import { studentApiStream } from './client';
import type { AssistantPersistenceOutcome, Citation } from '../types/domain';
import { parseProfCitationV1, type ProfCitationV1 } from '../types/prof-citation';
import type { ProfCheckpointEncounter, ProfCheckpointResult } from './prof-checkpoint';
import { parseProfFigureEvent } from '../types/prof-figure';

export interface ExplainStreamEvents {
  onToken: (chunk: string) => void;
  onReplaceText?: (fullText: string) => void;
  onCitations: (citations: Citation[]) => void;
  onDone: () => void;
  onRefused?: (reason: string, message: string, suggestions?: string[]) => void;
  onError?: (error: string) => void;
}

export interface ExplainStreamRequest {
  question_id: string;
  student_answer: boolean;  // true | false (the V/F choice)
  // Post-GATE-4.6 (2026-04-29): 'fast_practice' added so the Fast Section 5
  // mini-pratica wrong-answer Prof bubble doesn't dangle a follow-up offer
  // the student can't act on (Fast auto-advances + has no input box).
  mode: 'allena' | 'simula_review' | 'fast_practice';
  session_id?: string;
  response_time_ms?: number;
}

// ---------------------------------------------------------------------------
// explainWrongAnswerStream
// ---------------------------------------------------------------------------

export async function explainWrongAnswerStream(
  req: ExplainStreamRequest,
  events: ExplainStreamEvents,
  signal?: AbortSignal,
): Promise<void> {
  const init = {
    method: 'POST' as const,
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(req),
    signal,
  };
  for await (const msg of studentApiStream('/api/explain-wrong-answer-stream', init)) {
    switch (msg.event) {
      case 'token':
        events.onToken((msg.data as { text: string }).text);
        break;
      case 'replace_text':
        events.onReplaceText?.((msg.data as { text: string }).text);
        break;
      case 'citations':
        events.onCitations(msg.data as Citation[]);
        break;
      case 'done':
        events.onDone();
        return;
      case 'refused':
        events.onRefused?.(
          (msg.data as { refusal_reason?: string }).refusal_reason ?? 'unknown',
          (msg.data as { message?: string }).message ?? '',
          (msg.data as { suggestions?: string[] }).suggestions,
        );
        return;
      case 'error':
        events.onError?.((msg.data as { error?: string }).error ?? 'unknown');
        return;
    }
  }
}

// ---------------------------------------------------------------------------
// chiediStream — free-form question to Il Prof
// ---------------------------------------------------------------------------

export interface ChiediHistoryMessage {
  role: 'user' | 'prof';
  text: string;
}

export interface ChiediTopicState {
  primary?: string;
  visited?: string[];
  open?: string[];
}

export interface ChiediStreamRequest {
  figureContext?: import('../types/prof-figure').ProfFigureCommand;
  question: string;
  /** Exact assistant-row id generated before streaming for durable feedback. */
  responseId?: string;
  history?: ChiediHistoryMessage[];
  responseMode?: 'text' | 'voice';
  /** FP7 §1 (Mode B): true when this turn was dictated as a hold-to-talk VOICE
   *  NOTE. Routes through the normal Prof v2 text path but tells the BE to add
   *  `CANALE: nota vocale` so the CTRL `spoken` ≤300-char summary comes back in
   *  the `done` payload for the FE to speak. Only the v2 BE path reads it. */
  voiceNote?: boolean;
  topicHint?: {
    argomentoId: string;
    argomentoName?: string;
    evidence?: {
      principlesObserved: number;
      principlesTotal: number;
      principlesMastered: number;
      readinessPct: number;
      evidenceLabel: 'Non ancora osservato' | 'Pochi dati' | 'Da rinforzare' | 'In consolidamento' | 'Buona base';
      weaknessReason: 'observed_errors' | 'low_coverage' | 'mixed' | 'none';
    };
  };
  /** W3 Phase B: BE-issued chat-thread session id. Pass it on every turn
   *  after the first so the BE persists turns into the same chiedi_messages
   *  thread. Omit on the very first message of a fresh conversation — the
   *  BE will create a new sessions row and emit its id via the `session`
   *  SSE event. */
  sessionId?: string;
  /** Bounded continuity hint echoed by Prof v2. The server remains authoritative
   *  for an existing session; this only restores a new/legacy session safely. */
  topicState?: ChiediTopicState;
  /** Item 2 (founder round-3, 2026-06-11): the IN-LESSON Prof chat passes the
   *  student's current lesson position so the FULL Prof answers WITH a lesson
   *  BIAS (anchored, never refuses a driving/exam question), not lesson-LIMITED.
   *  Only the v2 BE path reads it. Absent on the global /prof thread. */
  lessonContext?: {
    argomentoId: string;
    argomentoName?: string;
    lessonTitle?: string;
    chapterTitle?: string;
    chapterOrder?: number;
    chapterCount?: number;
    visibleAssetRefs?: string[];
    questionRefs?: string[];
  };
}

/** Streaming events for the free-form Prof surface. */
export interface ChiediStreamEvents {
  onFigureContext?: (event: import('../types/prof-figure').ProfFigureEvent) => void;
  /** W3 Phase B: emitted ONCE before any token, carrying the BE chat-thread
   *  session id. The FE caches it via setChiediSessionId() so subsequent
   *  turns post the same id back. */
  onSession?: (sessionId: string) => void;
  onToken: (chunk: string) => void;
  onReplaceText?: (fullText: string) => void;
  /** Canonical Appendice provenance for the validated answer. */
  onCitations?: (citations: ProfCitationV1[]) => void;
  /** Contextual follow-up questions surfaced after each successful answer. */
  onFollowUps?: (questions: string[]) => void;
  /** FP7 §1 — the `done` payload now carries the optional voice-note fields
   *  (`spoken` ≤300-char summary + `voice_note` flag) so the FE can speak the
   *  summary. Back-compatible: existing call sites ignore the argument. */
  onDone: (done?: ChiediStreamDone) => void;
  onRefused?: (reason: string, message: string, suggestions?: string[]) => void;
  onError?: (error: string) => void;
  /** OA1 — a server auto-issued checkpoint encounter for THIS turn's answer,
   *  emitted before `done`. The FE renders the V/F card without any tap. */
  onCheckpoint?: (encounter: ProfCheckpointEncounter) => void;
  /** OA1 — the server graded the student's typed/dictated answer against the
   *  pending encounter (exact-once). Emitted before the model's explanation
   *  streams; the card renders already-complete. */
  onCheckpointResult?: (result: ProfCheckpointResult, questionText: string | null) => void;
}

/** FP7 §1 — the subset of the v2 `done` payload the FE reads. */
export interface ChiediTokenUsage {
  input: number;
  output: number;
  cache_read: number;
  cache_write: number;
}

export interface ChiediProviderCall {
  purpose: string;
  provider: 'anthropic' | 'openai';
  model: string;
  tokens: ChiediTokenUsage;
}

export interface ChiediStreamDone {
  response_id?: string;
  response_persisted?: boolean;
  assistant_persistence_outcome?: AssistantPersistenceOutcome;
  engine?: 'v1' | 'v2';
  model_used?: string;
  model_path?: string;
  tokens?: ChiediTokenUsage;
  provider_calls?: ChiediProviderCall[];
  actual_usage_complete?: boolean;
  telemetry_persisted?: boolean;
  telemetry_id?: string;
  /** True when the BE treated this as a voice-note turn. */
  voice_note?: boolean;
  /** The ≤300-char conversational summary to speak aloud (absent ⇒ FE falls back
   *  to the first paragraph of the visible answer). */
  spoken?: string;
  topic_state?: ChiediTopicState;
  context_version?: 'prof-context-v1';
  context_availability?: 'available' | 'degraded';
}

const MAX_ACCOUNTING_MODEL_CHARS = 160;
const MAX_ACCOUNTING_LABEL_CHARS = 80;
const MAX_ACCOUNTING_ID_CHARS = 128;
const MAX_PROVIDER_CALLS_PER_TURN = 8;

function sanitizeBoundedString(value: unknown, maxChars: number): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, maxChars) : undefined;
}

function sanitizeTokenUsage(value: unknown): ChiediTokenUsage | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  const fields = [raw.input, raw.output, raw.cache_read, raw.cache_write];
  if (!fields.every((field) => typeof field === 'number' && Number.isFinite(field) && field >= 0)) {
    return undefined;
  }
  return {
    input: raw.input as number,
    output: raw.output as number,
    cache_read: raw.cache_read as number,
    cache_write: raw.cache_write as number,
  };
}

function sanitizeProviderCalls(value: unknown): ChiediProviderCall[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const calls = value.slice(0, MAX_PROVIDER_CALLS_PER_TURN).flatMap((item): ChiediProviderCall[] => {
    if (typeof item !== 'object' || item === null || Array.isArray(item)) return [];
    const raw = item as Record<string, unknown>;
    const purpose = sanitizeBoundedString(raw.purpose, MAX_ACCOUNTING_LABEL_CHARS);
    const model = sanitizeBoundedString(raw.model, MAX_ACCOUNTING_MODEL_CHARS);
    const tokens = sanitizeTokenUsage(raw.tokens);
    if (!purpose || (raw.provider !== 'anthropic' && raw.provider !== 'openai') || !model || !tokens) return [];
    return [{ purpose, provider: raw.provider, model, tokens }];
  });
  return calls.length > 0 ? calls : undefined;
}

function sanitizeTopicState(value: unknown): ChiediTopicState | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  const topic: ChiediTopicState = {};
  if (typeof raw.primary === 'string' && raw.primary.trim()) {
    topic.primary = raw.primary.trim().slice(0, 120);
  }
  if (Array.isArray(raw.visited)) {
    const visited = raw.visited
      .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
      .slice(0, 40)
      .map((item) => item.trim().slice(0, 120));
    if (visited.length > 0) topic.visited = visited;
  }
  if (Array.isArray(raw.open)) {
    const open = raw.open
      .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
      .slice(0, 20)
      .map((item) => item.trim().slice(0, 160));
    if (open.length > 0) topic.open = open;
  }
  return Object.keys(topic).length > 0 ? topic : undefined;
}

export async function chiediStream(
  req: ChiediStreamRequest,
  events: ChiediStreamEvents,
  signal?: AbortSignal,
): Promise<void> {
  const topicState = sanitizeTopicState(req.topicState);
  const init = {
    method: 'POST' as const,
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ...req,
      ...(topicState ? { topicState } : {}),
    }),
    signal,
  };
  for await (const msg of studentApiStream('/api/chiedi', init)) {
    switch (msg.event) {
      case 'figure_context': {
        const event = parseProfFigureEvent(msg.data);
        if (event) events.onFigureContext?.(event);
        break;
      }
      case 'session': {
        // W3 Phase B: the BE-issued chat-thread session id, emitted exactly
        // once before any token. Tolerate both string and {id} payload
        // shapes for forward-compat (the BE today sends {id: <uuid>}).
        const data = msg.data as { id?: string } | string;
        const id = typeof data === 'string' ? data : data.id;
        if (id) events.onSession?.(id);
        break;
      }
      case 'token':
        events.onToken((msg.data as { text: string }).text);
        break;
      case 'replace_text':
        events.onReplaceText?.((msg.data as { text: string }).text);
        break;
      case 'citations': {
        const citations = Array.isArray(msg.data)
          ? msg.data.flatMap((value) => {
              const parsed = parseProfCitationV1(value);
              return parsed ? [parsed] : [];
            })
          : [];
        events.onCitations?.(citations);
        break;
      }
      case 'follow_ups':
        events.onFollowUps?.((msg.data as { questions?: string[] }).questions ?? []);
        break;
      case 'checkpoint': {
        const encounter = (msg.data as { encounter?: Record<string, unknown> }).encounter;
        if (
          encounter
          && typeof encounter.id === 'string'
          && typeof encounter.questionId === 'string'
          && typeof encounter.questionText === 'string'
          && typeof encounter.expiresAt === 'string'
        ) {
          events.onCheckpoint?.({
            id: encounter.id,
            questionId: encounter.questionId,
            questionText: encounter.questionText,
            expiresAt: encounter.expiresAt,
          });
        }
        break;
      }
      case 'checkpoint_result': {
        const raw = msg.data as { result?: Record<string, unknown>; questionText?: unknown };
        const result = raw.result;
        if (
          result
          && typeof result.encounterId === 'string'
          && typeof result.studentAnswer === 'boolean'
          && typeof result.correctAnswer === 'boolean'
          && typeof result.correct === 'boolean'
          && typeof result.principleId === 'string'
          && typeof result.argomentoId === 'string'
          && typeof result.completedAt === 'string'
        ) {
          events.onCheckpointResult?.(
            {
              encounterId: result.encounterId,
              studentAnswer: result.studentAnswer,
              correctAnswer: result.correctAnswer,
              correct: result.correct,
              principleId: result.principleId,
              argomentoId: result.argomentoId,
              graded: true,
              completedAt: result.completedAt,
            },
            typeof raw.questionText === 'string' ? raw.questionText : null,
          );
        }
        break;
      }
      case 'done': {
        const raw = typeof msg.data === 'object' && msg.data !== null
          ? msg.data as Record<string, unknown>
          : undefined;
        const safeDone: ChiediStreamDone = {};
        if (typeof raw?.response_id === 'string') safeDone.response_id = raw.response_id;
        if (typeof raw?.response_persisted === 'boolean') safeDone.response_persisted = raw.response_persisted;
        if (
          raw?.assistant_persistence_outcome === 'persisted'
          || raw?.assistant_persistence_outcome === 'recovered_duplicate'
          || raw?.assistant_persistence_outcome === 'locally_retained_unsaved'
          || raw?.assistant_persistence_outcome === 'failed'
        ) {
          safeDone.assistant_persistence_outcome = raw.assistant_persistence_outcome;
        }
        if (raw?.engine === 'v1' || raw?.engine === 'v2') safeDone.engine = raw.engine;
        const modelUsed = sanitizeBoundedString(raw?.model_used, MAX_ACCOUNTING_MODEL_CHARS);
        if (modelUsed) safeDone.model_used = modelUsed;
        const modelPath = sanitizeBoundedString(raw?.model_path, MAX_ACCOUNTING_LABEL_CHARS);
        if (modelPath) safeDone.model_path = modelPath;
        const doneTokens = sanitizeTokenUsage(raw?.tokens);
        if (doneTokens) safeDone.tokens = doneTokens;
        const providerCalls = sanitizeProviderCalls(raw?.provider_calls);
        if (providerCalls) safeDone.provider_calls = providerCalls;
        if (typeof raw?.actual_usage_complete === 'boolean') safeDone.actual_usage_complete = raw.actual_usage_complete;
        if (typeof raw?.telemetry_persisted === 'boolean') safeDone.telemetry_persisted = raw.telemetry_persisted;
        const telemetryId = sanitizeBoundedString(raw?.telemetry_id, MAX_ACCOUNTING_ID_CHARS);
        if (telemetryId) safeDone.telemetry_id = telemetryId;
        if (typeof raw?.voice_note === 'boolean') safeDone.voice_note = raw.voice_note;
        if (typeof raw?.spoken === 'string') safeDone.spoken = raw.spoken;
        const doneTopicState = sanitizeTopicState(raw?.topic_state);
        if (doneTopicState) safeDone.topic_state = doneTopicState;
        if (raw?.context_version === 'prof-context-v1') safeDone.context_version = raw.context_version;
        if (raw?.context_availability === 'available' || raw?.context_availability === 'degraded') {
          safeDone.context_availability = raw.context_availability;
        }
        events.onDone(Object.keys(safeDone).length > 0 ? safeDone : undefined);
        return;
      }
      case 'refused':
        events.onRefused?.(
          (msg.data as { refusal_reason?: string }).refusal_reason ?? 'unknown',
          (msg.data as { message?: string }).message ?? '',
          (msg.data as { suggestions?: string[] }).suggestions,
        );
        return;
      case 'error':
        events.onError?.((msg.data as { error?: string }).error ?? 'unknown');
        return;
    }
  }
}
